import { useForm, getFormProps, getInputProps } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useId, useState } from 'react'
import { useFetcher } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { toast } from 'sonner'
import { z } from 'zod'
import { ErrorList } from './forms.tsx'
import { Icon } from './ui/icon.tsx'

const NewsletterSchema = z.object({
	email: z.string().email('Voer een geldig e-mailadres in'),
	source: z.string().optional(),
})

export function NewsletterSignup({
	source = 'exhibitions',
}: {
	source?: string
}) {
	const fetcher = useFetcher()
	const formId = useId()
	const [isSubscribed, setIsSubscribed] = useState(false)

	const [form, fields] = useForm({
		id: formId,
		constraint: getZodConstraint(NewsletterSchema),
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: NewsletterSchema })
		},
		shouldValidate: 'onBlur',
		shouldRevalidate: 'onInput',
	})

	const isSubmitting = fetcher.state !== 'idle'

	if (fetcher.data?.success && !isSubscribed) {
		setIsSubscribed(true)
		toast.success('Je bent aangemeld.')
	}

	if (isSubscribed) {
		return (
			<div
				className="p-10 text-center"
				style={{ background: 'var(--ink)', color: 'var(--paper)' }}
			>
				<div
					className="mx-auto mb-5 inline-flex h-12 w-12 items-center justify-center rounded-full"
					style={{ background: 'rgba(250,248,244,0.15)' }}
				>
					<Icon name="check" className="h-6 w-6" />
				</div>
				<h3
					className="font-heading mb-2 text-xl font-medium italic"
				>
					Aangemeld
				</h3>
				<p className="text-sm opacity-70">
					Je ontvangt bericht bij nieuw werk en exposities.
				</p>
			</div>
		)
	}

	return (
		<div
			className="p-10"
			style={{ background: 'var(--ink)', color: 'var(--paper)' }}
		>
			<div className="mb-6">
				<div className="eyebrow mb-3" style={{ color: 'rgba(250,248,244,0.5)' }}>
					Nieuwsbrief
				</div>
				<h3
					className="font-heading mb-2 text-xl font-medium italic"
				>
					Nieuws uit het atelier
				</h3>
				<p className="text-sm opacity-70">
					Nieuw werk en exposities, vier keer per jaar.
				</p>
			</div>

			<fetcher.Form
				method="POST"
				action="/resources/newsletter-subscribe"
				{...getFormProps(form)}
			>
				<HoneypotInputs />
				<input type="hidden" name="source" value={source} />

				<div className="flex gap-2">
					<div className="flex-1">
						<input
							{...getInputProps(fields.email, { type: 'email' })}
							placeholder="je e-mailadres"
							disabled={isSubmitting}
							autoComplete="email"
							className="w-full rounded-full border px-5 py-3 text-[15px] outline-none"
							style={{
								borderColor: 'rgba(250,248,244,0.2)',
								background: 'rgba(250,248,244,0.08)',
								color: 'var(--paper)',
								fontFamily: 'var(--font-sans)',
							}}
						/>
						<ErrorList
							errors={fields.email.errors}
							id={fields.email.errorId}
						/>
					</div>

					<button
						type="submit"
						disabled={isSubmitting}
						className="shrink-0 rounded-full px-6 py-3 text-[15px] font-medium transition-opacity"
						style={{
							background: 'var(--paper)',
							color: 'var(--ink)',
							opacity: isSubmitting ? 0.6 : 1,
						}}
					>
						{isSubmitting ? '...' : 'Aanmelden'}
					</button>
				</div>
			</fetcher.Form>
		</div>
	)
}
